import type { PurchaseSummary } from '@aeris/contracts'
import type { StatusTone } from '@aeris/ui'

/**
 * Purchasing vocabulary, in Portuguese.
 *
 * `@aeris/purchasing` returns codes and never sentences: a pure package the
 * browser and the server both import must not carry an interface language, and
 * a code is what a caller branches on. The words live here, in one file, so the
 * list and the receipt dialog cannot end up calling the same state two
 * different things.
 */

type Status = PurchaseSummary['status']

export const STATUS_LABELS: Record<Status, string> = {
  draft: 'Rascunho',
  confirmed: 'Confirmada',
  received: 'Recebida',
  cancelled: 'Cancelada',
}

/**
 * The verb on the button, which is not the word for the state.
 *
 * "Confirmar" is what the person does; "Confirmada" is what the purchase then
 * is. A button reading "Confirmada" on a purchase nobody has confirmed is the
 * small confusion that makes somebody click twice.
 *
 * There is no entry for `received`, and that is the point: receiving is not a
 * status somebody sets. It is a delivery with quantities, and it has its own
 * dialog.
 */
export const TRANSITION_LABELS: Record<'confirmed' | 'cancelled', string> = {
  confirmed: 'Confirmar',
  cancelled: 'Cancelar',
}

/**
 * The colour a status carries.
 *
 * `confirmed` is a warning rather than an info: a confirmed purchase is one the
 * shop has committed to and is still WAITING for. It is the state that needs
 * chasing, and the list is where somebody notices that a delivery is late.
 */
export const STATUS_TONES: Record<Status, StatusTone> = {
  draft: 'neutral',
  confirmed: 'warning',
  received: 'success',
  cancelled: 'danger',
}

/** `Compra nº 12` — or `Compra (rascunho)` while it has no number yet. */
export const purchaseTitle = (purchase: { number: number | null }): string =>
  purchase.number === null
    ? 'Compra (rascunho)'
    : `Compra nº ${String(purchase.number)}`
